import { forwardRef, useState, type SelectHTMLAttributes } from 'react';
import { cn } from '../foundation/cn';
import { matchesSearch } from '../foundation/search';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
  /** D-213: extra text a `searchable` Select also matches (e.g. a company's tax id) — never shown. */
  readonly searchText?: string | null;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  readonly invalid?: boolean;
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  /**
   * D-213: a type-to-narrow box above the list, for long company/client pickers. Same rule as
   * every other search in the app (lib/search.ts): case-insensitive, partial, label OR searchText.
   * The currently selected option and any "none" option (value '') always stay listed, so
   * narrowing never silently changes or clears the selection.
   */
  readonly searchable?: boolean;
  readonly searchPlaceholder?: string;
}

const SELECT_CLASS = cn(
  'h-10 w-full rounded-token border bg-bg px-3 text-sm text-fg',
  'transition-colors duration-fast ease-standard',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
  'disabled:cursor-not-allowed disabled:opacity-60',
);

// Native select (accessible, keyboard-friendly). A custom listbox is intentionally not
// built — native is the reliable foundation; `searchable` only narrows the native options.
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    invalid = false,
    options,
    placeholder,
    searchable = false,
    searchPlaceholder = 'ძიება: დასახელება ან საიდენტიფიკაციო კოდი…',
    className,
    ...rest
  },
  ref,
) {
  const [query, setQuery] = useState('');
  const selected = typeof rest.value === 'string' ? rest.value : null;
  const visible = searchable
    ? options.filter((o) => o.value === '' || o.value === selected || matchesSearch(query, o.label, o.searchText))
    : options;
  const noMatch = searchable && query.trim() !== '' && !options.some((o) => o.value !== '' && matchesSearch(query, o.label, o.searchText));

  const select = (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(SELECT_CLASS, invalid ? 'border-danger' : 'border-input-border', className)}
      {...rest}
    >
      {placeholder !== undefined && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {visible.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  if (!searchable) return select;

  return (
    <div className="flex w-full flex-col gap-1.5">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={searchPlaceholder}
        aria-label="სიაში ძიება"
        disabled={rest.disabled}
        className={cn(SELECT_CLASS, 'h-9 border-input-border placeholder:text-fg-muted')}
      />
      {select}
      {noMatch && (
        <p role="status" className="text-xs text-fg-muted">
          არაფერი მოიძებნა — შეამოწმეთ დასახელება ან საიდენტიფიკაციო კოდი.
        </p>
      )}
    </div>
  );
});
